/** Idle after the last scroll event before treating alignment as done. */
export const TOUR_SCROLL_SETTLE_MS = 100;
/** If no scroll event starts, assume the browser had nothing to move. */
export const TOUR_SCROLL_START_MS = 120;
/** Hard cap so a stuck smooth-scroll never blocks the hint. */
export const TOUR_SCROLL_MAX_MS = 700;

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * True when the target is already on-screen enough that a scroll would be a no-op.
 * Zero-size rects (unlaid-out / jsdom stubs) are treated as already aligned.
 */
export function isTourTargetInView(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return true;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tallerThanView = rect.height > vh;
  const widerThanView = rect.width > vw;

  if (tallerThanView || widerThanView) {
    const overlapH = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
    const overlapW = Math.min(rect.right, vw) - Math.max(rect.left, 0);
    return overlapH > 0 && overlapW > 0;
  }

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= vh &&
    rect.right <= vw
  );
}

function waitForScrollSettle(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    let settled = false;
    let seenScroll = false;
    let idleTimer = 0;

    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("scrollend", onScroll, true);
      signal?.removeEventListener("abort", finish);
      window.clearTimeout(idleTimer);
      window.clearTimeout(startTimer);
      window.clearTimeout(maxTimer);
      resolve();
    };

    const onScroll = () => {
      seenScroll = true;
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(finish, TOUR_SCROLL_SETTLE_MS);
    };

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("scrollend", onScroll, true);
    signal?.addEventListener("abort", finish);

    const startTimer = window.setTimeout(() => {
      if (!seenScroll) finish();
    }, TOUR_SCROLL_START_MS);
    const maxTimer = window.setTimeout(finish, TOUR_SCROLL_MAX_MS);
  });
}

/**
 * Scroll the target into view when it is off-screen, then resolve after the
 * scroll settles. Already-visible targets resolve immediately.
 */
export async function scrollTourTargetIntoView(
  element: HTMLElement,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) return;
  if (!element.isConnected || isTourTargetInView(element)) return;

  const behavior = prefersReducedMotion() ? "auto" : "smooth";
  element.scrollIntoView({
    block: "center",
    inline: "nearest",
    behavior,
  });

  if (behavior === "auto") {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
    });
    return;
  }

  await waitForScrollSettle(signal);
}
